import React from 'react';
import {
    Modal,
    View,
    Text,
    TouchableOpacity,
    StyleSheet,
    Platform,
    Pressable,
} from 'react-native';
import { Link, useRouter } from 'expo-router';
import { Trans } from '@lingui/react/macro';
import { signOutCurrentSession } from '../lib/supabase';
import { useSession } from '../lib/SessionContext';
import { canAccessMainApp } from '../utils/authHelpers';
import colors from '../lib/colors';
import { buildCanonicalDashboardPath } from '../lib/dashboardRoutes';
import { buildAccountSettingsPath } from '../lib/accountRoutes';
import { boundarySafeHref } from '../lib/i18n/publicWeb';
import LanguageSelector from './i18n/LanguageSelector';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';
import { documentationHref } from '../lib/i18n/documentation';


interface NavigationMenuProps {
    visible: boolean;
    onClose: () => void;
    onAuthRequired?: () => void;
    hideAuthAction?: boolean;
}

export default function NavigationMenu({
    visible,
    onClose,
    onAuthRequired,
    hideAuthAction = false,
}: NavigationMenuProps) {
    const router = useRouter();
    const { messageLocale } = useAppLocale();
    const { session } = useSession();
    const canAccessApp = canAccessMainApp(session);
    const settingsHref = boundarySafeHref(buildAccountSettingsPath());
    const dashboardHref = boundarySafeHref(buildCanonicalDashboardPath());
    const inboxHref = boundarySafeHref(buildCanonicalDashboardPath('/inbox'));

    const handleAuth = async () => {
        if (canAccessApp) {
            console.log('🚪 [NavigationMenu] Starting logout...');
            onClose();

            try {
                // Just sign out - let SubscriptionContext handle channel cleanup
                const { error } = await signOutCurrentSession();

                if (error) {
                    // If session is already missing, treat as successful logout
                    if (error.name === 'AuthSessionMissingError') {
                        console.log('🚪 [NavigationMenu] Session already missing, treating as logged out');
                    } else {
                        console.error('🚪 [NavigationMenu] Error during sign out:', error);
                    }
                } else {
                    console.log('🚪 [NavigationMenu] Successfully signed out');
                }
                router.replace('/');
            } catch (err) {
                console.error('🚪 [NavigationMenu] Exception during logout:', err);
                router.replace('/');
            }
        } else {
            // Sign-in logic remains the same
            onAuthRequired?.();
            onClose();
        }
    };

    return (
        <Modal
            visible={visible}
            animationType="fade"
            transparent
            onRequestClose={onClose}
        >
            <View style={styles.backdrop}>
                <Pressable onPress={onClose} style={styles.backdropTouchable} />
                <View style={styles.menuContainer}>
                    <View style={styles.menuHeader}>
                        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                            <Text style={styles.closeButtonText}>✕</Text>
                        </TouchableOpacity>
                    </View>

                    <View style={styles.menuItems}>
                        {canAccessApp && (
                            <>
                                <Link href={dashboardHref} asChild>
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                        testID="mobile-menu-dashboard-button"
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.dashboard" comment="UI copy in components/NavigationMenu.tsx">Dashboard</Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link href={inboxHref} asChild>
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                        testID="mobile-menu-inbox-button"
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.inbox" comment="UI copy in components/NavigationMenu.tsx">Inbox</Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link
                                    href={documentationHref(messageLocale)}
                                    target="_blank"
                                    asChild
                                >
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.menuItemText}>
                                            <Trans id="navigation.docs">Docs</Trans>
                                        </Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link href={settingsHref} asChild>
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                        testID="mobile-menu-settings-button"
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.account" comment="UI copy in components/NavigationMenu.tsx">Account</Trans></Text>
                                    </TouchableOpacity>
                                </Link>
                            </>
                        )}

                        {!canAccessApp && (
                            <>
                                <Link href="/product" asChild>
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.product" comment="UI copy in components/NavigationMenu.tsx">Product</Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link href="/pricing" asChild>
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.pricing" comment="UI copy in components/NavigationMenu.tsx">Pricing</Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link href="/research-briefs" asChild>
                                    <TouchableOpacity style={styles.menuItem} onPress={onClose} activeOpacity={0.7}>
                                        <Text style={styles.menuItemText}><Trans>Research briefs</Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link href="/agents" asChild>
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.agents" comment="UI copy in components/NavigationMenu.tsx">Agents</Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                <Link
                                    href={documentationHref(messageLocale)}
                                    target="_blank"
                                    asChild
                                >
                                    <TouchableOpacity
                                        style={styles.menuItem}
                                        onPress={onClose}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.menuItemText}><Trans id="navigationmenu.docs" comment="UI copy in components/NavigationMenu.tsx">Docs</Trans></Text>
                                    </TouchableOpacity>
                                </Link>
                            </>
                        )}

                        <LanguageSelector variant="mobile" onNavigate={onClose} />

                        {!hideAuthAction && (
                            <TouchableOpacity
                                style={styles.menuItem}
                                onPress={handleAuth}
                                activeOpacity={0.7}
                                testID="mobile-menu-auth-button"
                            >
                                <Text style={styles.menuItemText}>
                                    {canAccessApp ? <Trans id="navigationmenu.signOut" comment="Conditional UI copy in components/NavigationMenu.tsx">Sign Out</Trans> : <Trans id="navigationmenu.signUp" comment="Conditional UI copy in components/NavigationMenu.tsx">Sign up</Trans>}
                                </Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
    },
    backdropTouchable: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    menuContainer: {
        backgroundColor: 'white',
        width: 280,
        maxWidth: '88%',
        height: '100%',
        elevation: 5,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOffset: { width: -2, height: 0 },
            shadowOpacity: 0.25,
            shadowRadius: 5,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '-2px 0 5px rgba(0,0,0,0.25)',
        }),
    },
    menuHeader: {
        paddingTop: Platform.OS === 'web' ? 20 : 60,
        paddingHorizontal: 20,
        paddingBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
    },
    closeButton: {
        alignSelf: 'flex-end',
        padding: 8,
        marginRight: -8,
        marginTop: -8,
    },
    closeButtonText: {
        fontSize: 24,
        color: '#6b7280',
        fontWeight: '300',
    },
    menuItems: {
        paddingTop: 14,
    },
    menuItem: {
        paddingVertical: 15,
        paddingHorizontal: 24,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            ':hover': {
                backgroundColor: '#f9fafb',
            },
        }),
    },
    menuItemText: {
        fontSize: 16,
        color: colors.primaryGreenDark,
        fontWeight: '500',
    },
});
